import { useEffect, useRef, useState } from "react";

import {
  accountErrorMessage,
  approveDeviceKeyRotation,
  approveDeviceLink,
  consumeBrowserReviewHandoff,
  type BrowserReviewContext,
} from "../../lib/account-client";
import { ProjectRevisionHistory } from "./ProjectRevisionHistory";

export type CapturedDeviceApproval =
  | { kind: "link"; linkId: string; approvalCode: string; scope: "project" | "workspace" }
  | { kind: "rotation"; rotationId: string; approvalCode: string }
  | { kind: "handoff"; handoff: string; expectedProjectId: string }
  | { kind: "invalid"; route: "link" | "rotation" | "handoff" };

export interface DeviceBrowserApprovalApi {
  approveDeviceLink: typeof approveDeviceLink;
  approveDeviceKeyRotation: typeof approveDeviceKeyRotation;
  consumeBrowserReviewHandoff: typeof consumeBrowserReviewHandoff;
}

export const deviceBrowserApprovalApi: DeviceBrowserApprovalApi = {
  approveDeviceLink,
  approveDeviceKeyRotation,
  consumeBrowserReviewHandoff,
};

export function captureDeviceApprovalFragment(pathname = window.location.pathname): CapturedDeviceApproval | null {
  const link = pathname === "/manage/devices/link";
  const rotation = pathname === "/manage/devices/rotate-key";
  const handoffMatch = pathname.match(/^\/manage\/projects\/([^/]+)\/review$/);
  if (!link && !rotation && !handoffMatch) return null;
  const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}`);
  if (link) {
    const linkIds = fragment.getAll("link");
    const codes = fragment.getAll("code");
    const scopes = fragment.getAll("scope");
    const validKeys = [...fragment.keys()].every((key) => key === "link" || key === "code" || key === "scope");
    if (!validKeys || linkIds.length !== 1 || codes.length !== 1 || scopes.length !== 1
      || !safeFragmentValue(linkIds[0]!) || !safeFragmentValue(codes[0]!)
      || (scopes[0] !== "project" && scopes[0] !== "workspace")) return { kind: "invalid", route: "link" };
    return { kind: "link", linkId: linkIds[0]!, approvalCode: codes[0]!, scope: scopes[0] };
  }
  if (rotation) {
    const ids = fragment.getAll("rotation");
    const codes = fragment.getAll("code");
    const validKeys = [...fragment.keys()].every((key) => key === "rotation" || key === "code");
    if (!validKeys || ids.length !== 1 || codes.length !== 1
      || !safeFragmentValue(ids[0]!) || !safeFragmentValue(codes[0]!)) return { kind: "invalid", route: "rotation" };
    return { kind: "rotation", rotationId: ids[0]!, approvalCode: codes[0]! };
  }
  const handoffs = fragment.getAll("handoff");
  const validKeys = [...fragment.keys()].every((key) => key === "handoff");
  let projectId: string;
  try {
    projectId = decodeURIComponent(handoffMatch![1]!);
  } catch {
    return { kind: "invalid", route: "handoff" };
  }
  if (!validKeys || handoffs.length !== 1 || !safeFragmentValue(handoffs[0]!) || !projectId) {
    return { kind: "invalid", route: "handoff" };
  }
  return { kind: "handoff", handoff: handoffs[0]!, expectedProjectId: projectId };
}

export function DeviceBrowserApprovalPage({
  approval,
  api = deviceBrowserApprovalApi,
}: {
  approval: CapturedDeviceApproval | null;
  api?: DeviceBrowserApprovalApi;
}) {
  const [pending, setPending] = useState(false);
  const [complete, setComplete] = useState(false);
  const [review, setReview] = useState<BrowserReviewContext | null>(null);
  const [error, setError] = useState<string | null>(approval?.kind === "invalid" || !approval
    ? "This private approval link is incomplete or has already been cleared. Start again from the Doorway Mac app."
    : null);
  const actionRef = useRef<HTMLButtonElement>(null);
  const errorRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => { if (error) errorRef.current?.focus(); else actionRef.current?.focus(); }, [error]);

  const approve = async () => {
    if (!approval || approval.kind === "invalid") return;
    setPending(true);
    setError(null);
    try {
      if (approval.kind === "link") {
        await api.approveDeviceLink(approval);
        setComplete(true);
      } else if (approval.kind === "rotation") {
        await api.approveDeviceKeyRotation(approval);
        setComplete(true);
      } else {
        const consumed = await api.consumeBrowserReviewHandoff(approval.handoff);
        if (consumed.projectId !== approval.expectedProjectId) {
          throw new Error("The browser handoff does not match this project review page.");
        }
        setReview(consumed);
        setComplete(true);
      }
    } catch (cause) {
      setError(accountErrorMessage(cause, "Doorway could not complete this browser approval."));
    } finally {
      setPending(false);
    }
  };

  if (error) return (
    <main className="workspace-gate">
      <section className="flow-card review-stack" aria-labelledby="device-approval-error">
        <h1 id="device-approval-error">Approval could not continue</h1>
        <p ref={errorRef} tabIndex={-1} role="alert">{error}</p>
        <p>No device access, key rotation, publication, or source selection was approved.</p>
      </section>
    </main>
  );
  if (!approval || approval.kind === "invalid") return null;

  return (
    <main className="workspace-gate">
      <section className="flow-card review-stack" aria-labelledby="device-browser-approval">
        <p className="step-label">Human browser approval</p>
        <h1 id="device-browser-approval">{approvalTitle(approval.kind)}</h1>
        {approval.kind === "link" ? (
          <p>{approval.scope === "workspace"
            ? "This device requested access to sync projects across the workspace. Only the workspace owner can approve it."
            : "This device requested access to sync and inspect drafts for one project."}</p>
        ) : approval.kind === "rotation" ? (
          <p>Approve the proof-of-possession key change. Existing device tokens stop working after the Mac app completes exchange.</p>
        ) : (
          <p>Consume this one-time, digest-bound handoff to review the exact source snapshot. Publishing and sensitive controls remain separate browser actions.</p>
        )}
        {!complete ? (
          <button ref={actionRef} type="button" disabled={pending} onClick={() => void approve()}>
            {pending ? "Checking exact approval..." : approvalButton(approval.kind)}
          </button>
        ) : <p role="status" tabIndex={-1}><strong>{approvalComplete(approval.kind)}</strong></p>}
        {review ? (
          <div className="review-stack">
            <p>Snapshot <code>{review.snapshotId}</code></p>
            <p>Source digest <code>{review.sourceDigest}</code></p>
            <ProjectRevisionHistory workspaceId={review.workspaceId} projectId={review.projectId} />
          </div>
        ) : null}
      </section>
    </main>
  );
}

function safeFragmentValue(value: string): boolean {
  return value.length >= 1 && value.length <= 512 && /^[A-Za-z0-9._~-]+$/.test(value);
}

function approvalTitle(kind: Exclude<CapturedDeviceApproval["kind"], "invalid">): string {
  return ({ link: "Review device access", rotation: "Review device key rotation", handoff: "Open exact source review" } as const)[kind];
}

function approvalButton(kind: Exclude<CapturedDeviceApproval["kind"], "invalid">): string {
  return ({ link: "Approve device access", rotation: "Approve key rotation", handoff: "Consume one-time review handoff" } as const)[kind];
}

function approvalComplete(kind: Exclude<CapturedDeviceApproval["kind"], "invalid">): string {
  return ({
    link: "Device access approved. Return to the Mac app to complete proof exchange.",
    rotation: "Key rotation approved. Return to the Mac app to complete proof exchange.",
    handoff: "The one-time handoff was consumed for this exact snapshot.",
  } as const)[kind];
}
